import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import { searchTestsBySymptom } from "../../services/LabTestApi/SymptomsDetailsApi";
import { useCartActions } from "../../hooks/useCartActions";
import { fetchAllRatings } from "../../Redux/ratingSlice";
import { 
  fetchWishlist, 
  addToWishlist, 
  removeFromWishlist,
  addWishlistOptimistic,
  removeWishlistOptimistic,
} from "../../Redux/wishlistSlice";

import { Search } from "lucide-react";
import { 
  FaFire, 
  FaStar, 
  FaFlask, 
  FaHeart, 
  FaRegHeart,
  FaChevronRight,
  FaShoppingCart,
} from "react-icons/fa";
import TestDetails from "../../component/LabTest/TestDetails";
import LoginPopup from "../common/LoginPopup";
import Coupon from "../Coupon/Coupon";

// ✅ IMAGE BASE URL
const API_BASE = (process.env.REACT_APP_API_URL || 'https://online-tech.in/api').replace(/\/+$/, '');
const BASE_IMG = API_BASE?.replace('/api', '');

export default function SymptomsDetailsPage() {
  const dispatch = useDispatch();
  const location = useLocation();
  const symptom = location.state?.symptom;
  const symptomId = symptom?.id;

  const cart = useSelector((state) => state.cart.items || []);
  const ratings = useSelector((state) => state.rating.ratings || {});
  const ratingLoading = useSelector((state) => state.rating.loading);
  const wishlistItems = useSelector((state) => state.wishlist.items || []);
  const wishlistLoading = useSelector((state) => state.wishlist.loading);

  const {
    addWithReplaceCheck,
    removeItem,
    LoginPopupComponent,
  } = useCartActions();

  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTestId, setSelectedTestId] = useState(null);
  const [imgError, setImgError] = useState({});
  const [showLoginPopup, setShowLoginPopup] = useState(false);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const testsPerPage = 10;

  // ✅ Get User ID from localStorage
  const getUserId = () => {
    try {
      const userData = JSON.parse(localStorage.getItem("medarphaUser") || "{}");
      return userData?.userId || null;
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
      return null;
    }
  };

  // ✅ Check if in wishlist
  const isInWishlist = (productId, categoryId = 2) => {
    if (!wishlistItems || !Array.isArray(wishlistItems)) return false;
    return wishlistItems.some(
      item => Number(item.productId) === Number(productId) && 
              Number(item.categoryId) === Number(categoryId)
    );
  };

  // ✅ Load Wishlist
  const loadWishlist = async () => {
    const userId = getUserId();
    if (!userId) return;
    try {
      await dispatch(fetchWishlist(userId)).unwrap();
    } catch (error) {
      console.error('Error loading wishlist:', error);
    }
  };

  // ✅ Toggle Wishlist
  const toggleWishlist = async (test, e) => {
    e.stopPropagation();
    const userId = getUserId();
    
    if (!userId) {
      setShowLoginPopup(true);
      return;
    }

    const productId = test?.testID || test?.id;
    const categoryId = 2;
    const isWished = isInWishlist(productId, categoryId);

    if (isWished) {
      dispatch(removeWishlistOptimistic({ productId, categoryId }));
    } else {
      dispatch(addWishlistOptimistic({ productId, categoryId }));
    }

    try {
      if (isWished) {
        await dispatch(removeFromWishlist({ userId, productId, categoryId })).unwrap();
      } else {
        await dispatch(addToWishlist({ userId, productId, categoryId })).unwrap();
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      if (isWished) {
        dispatch(addWishlistOptimistic({ productId, categoryId }));
      } else {
        dispatch(removeWishlistOptimistic({ productId, categoryId }));
      }
      alert('Failed to update wishlist. Please try again.');
    }
  };

  // ================= FETCH TESTS & RATINGS =================
  useEffect(() => {
    if (!symptomId) return;

    const load = async () => {
      try {
        setLoading(true);
        const data = await searchTestsBySymptom(symptomId);
        const testsList = Array.isArray(data) ? data : [];
        setTests(testsList);

        if (testsList.length > 0) {
          const ratingProducts = testsList.map((test) => {
            const id = test?.testID || test?.id;
            return { productId: id, categoryId: 2 };
          });
          await dispatch(fetchAllRatings(ratingProducts));
        }

        await loadWishlist();

      } catch (e) {
        console.log(e);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [symptomId, dispatch]);

  // ================= FILTER & PAGINATE =================
  const filteredTests = tests.filter((t) =>
    t?.testName?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filteredTests.length / testsPerPage);
  const paginatedTests = filteredTests.slice(
    (currentPage - 1) * testsPerPage,
    currentPage * testsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  // ================= RATING HELPER =================
  const getTestRating = (testId) => {
    const key = `${testId}_2`;
    return ratings[key] || { average: 0, total: 0, ratings: [] };
  };

  const renderStars = (rating = 0) => {
    const fullStars = Math.floor(rating);
    return [...Array(5)].map((_, i) =>
      i < fullStars ? (
        <FaStar key={i} size={12} color="#ffc107" />
      ) : (
        <FaStar key={i} size={12} color="#ddd" />
      )
    );
  };

  // ================= CART HELPERS =================
  const isInCart = (testId) =>
    cart.some((item) => Number(item.productId) === Number(testId) && Number(item.categoryId) === 2);

  const buildCartItem = (test) => {
    const id = test?.testID || test?.id;
    const originalPrice = Number(test?.testPrice || test?.TestPrice) || 0;
    const sellingPrice = Number(test?.sellingPrice || test?.SellingPrice) || originalPrice;
    const discount = Number(test?.discount || test?.Discount) || 0;

    return {
      productId: id,
      categoryId: 2,
      name: test?.testName || test?.TestName,
      price: sellingPrice,
      quantity: 1,
      storeId: test?.labId || test?.LabId || 0,
      originalPrice,
      discount,
    };
  };

  // ================= IMAGE HELPER =================
  const getTestImageUrl = (test) => {
    const testImage = test?.testImage || test?.TestImage;
    if (testImage) {
      if (testImage.startsWith('http')) {
        return testImage;
      }
      return `${BASE_IMG}/TestMasterImage/${testImage}`;
    }
    return null;
  };

  const getLabImageUrl = (test) => {
    const labImage = test?.LabImage || test?.labImage || test?.lab_image;
    if (labImage) {
      if (labImage.startsWith('http')) {
        return labImage;
      }
      return `${BASE_IMG}/LabMasterImage/${labImage}`;
    }
    return null;
  };

  // ================= LOADER =================
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ================= UI =================
  return (
    <div className="px-4 md:px-8 py-6 bg-gradient-to-br from-gray-50 via-white to-red-50/30 rounded-xl shadow-lg mt-3">

      {/* HEADER */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-1.5 rounded-full shadow-md mb-3">
          <FaFire size={12} className="text-yellow-400" />
          <span className="text-xs font-bold tracking-wide">TESTS FOR SYMPTOM</span>
          <FaStar size={12} className="text-yellow-400" />
        </div>

        <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold">
          <span className="bg-gradient-to-r from-gray-800 to-gray-900 bg-clip-text text-transparent">
            {symptom?.name || "Symptom"}
          </span>
          <span className="text-red-600 ml-2">Tests</span>
        </h2>

        <p className="text-gray-500 text-sm mt-2 max-w-md mx-auto">
          {tests.length} tests available • Book with confidence • Accurate results
        </p>

        {/* Search Bar */}
        <div className="flex justify-center mt-6">
          <div className="relative w-full max-w-md">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search tests..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 text-sm rounded-full border border-gray-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent bg-white/80 backdrop-blur-sm"
            />
          </div>
        </div>

        {filteredTests.length > 0 && (
          <p className="text-xs text-gray-400 mt-3">
            Showing {paginatedTests.length} of {filteredTests.length} tests
          </p>
        )}
      </div>

      {/* ✅ GRID - Same as PopularTests */}
      {filteredTests.length === 0 ? (
        <div className="text-center py-10">
          <div className="text-4xl mb-3">🔍</div>
          <p className="text-gray-500">No tests found for this symptom</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {paginatedTests.map((test) => {
              const id = test?.testID || test?.id;
              const inCart = isInCart(id);
              const inWishlist = isInWishlist(id, 2);

              const originalPrice = Number(test?.testPrice || test?.TestPrice) || 0;
              const sellingPrice = Number(test?.sellingPrice || test?.SellingPrice) || originalPrice;
              const discount = Number(test?.discount || test?.Discount) || 0;
              const hasDiscount = discount > 0 && sellingPrice < originalPrice;
              const discountPercent = originalPrice > 0 && discount > 0
                ? Math.round((discount / originalPrice) * 100)
                : 0;

              const isPriceAvailable = sellingPrice > 0 || originalPrice > 0;

              const { average: avgRating, total: totalReviews } = getTestRating(id);
              const isPopular = test?.IsPopular === true;

              const reportTime = test?.ReportTime || test?.reportTime;
              const reportTimeType = test?.ReportTimeType || test?.reportTimeType;
              let reportText = "";
              if (reportTime) {
                const unit = reportTimeType === 2 ? "days" : "hours";
                reportText = `⏱️ Reports in ${reportTime} ${unit}`;
              }

              // Image handling
              const testImageUrl = getTestImageUrl(test);
              const labImageUrl = getLabImageUrl(test);
              const errorState = imgError[id];

              let imageSrc = null;
              let showFallbackIcon = false;

              if (errorState === 'lab') {
                showFallbackIcon = true;
              } else if (errorState === 'test') {
                if (labImageUrl) {
                  imageSrc = labImageUrl;
                } else {
                  showFallbackIcon = true;
                }
              } else {
                if (testImageUrl) {
                  imageSrc = testImageUrl;
                } else {
                  if (labImageUrl) {
                    imageSrc = labImageUrl;
                  } else {
                    showFallbackIcon = true;
                  }
                }
              }

              const labId = test?.labId || test?.LabId;

              return (
                <div
                  key={id}
                  className="bg-white border rounded-xl p-3 h-[380px] flex flex-col hover:shadow-lg transition-all duration-300 hover:-translate-y-1 relative cursor-pointer"
                  onClick={() => setSelectedTestId(id)}
                >
                  {/* Wishlist Button */}
                  <button
                    onClick={(e) => toggleWishlist(test, e)}
                    className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-white/80 backdrop-blur-sm shadow-md hover:bg-white transition-all duration-200"
                    disabled={wishlistLoading}
                  >
                    {wishlistLoading ? (
                      <div className="w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      inWishlist ? (
                        <FaHeart size={14} className="text-red-500 fill-red-500" />
                      ) : (
                        <FaRegHeart size={14} className="text-gray-400 hover:text-red-500" />
                      )
                    )}
                  </button>

                  {/* Popular Badge */}
                  {isPopular && (
                    <div className="absolute top-2 left-2 z-10">
                      <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow-lg flex items-center gap-1">
                        <FaStar size={9} className="fill-white" /> POPULAR
                      </div>
                    </div>
                  )}

                  {/* Discount Badge */}
                  {hasDiscount && discount > 0 && isPriceAvailable && (
                    <div className="absolute top-8 left-2 z-10">
                      <div className="bg-gradient-to-r from-red-500 to-red-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow-md">
                        {discountPercent}% OFF
                      </div>
                    </div>
                  )}

                  {/* Image - Zomato Style */}
                  <div
                    className="relative w-full h-[140px] cursor-pointer rounded-xl overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100"
                    onClick={() => setSelectedTestId(id)}
                  >
                    {!showFallbackIcon && imageSrc ? (
                      <img
                        src={imageSrc}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        alt={test?.testName}
                        onError={() => {
                          if (errorState === undefined && testImageUrl) {
                            setImgError(prev => ({ ...prev, [id]: 'test' }));
                          } else if (errorState === 'test' && labImageUrl) {
                            setImgError(prev => ({ ...prev, [id]: 'lab' }));
                          } else {
                            setImgError(prev => ({ ...prev, [id]: 'lab' }));
                          }
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FaFlask size={48} className="text-gray-300" />
                      </div>
                    )}
                    {/* Image Gradient Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-black/20 to-transparent"></div>
                  </div>

                  {/* Name */}
                  <div className="text-[14px] font-bold text-center line-clamp-2 mt-2.5 px-1 min-h-[40px] text-gray-800">
                    {test?.testName || test?.TestName}
                  </div>

                  {/* Ratings */}
                  <div className="flex justify-center items-center gap-1 mt-1">
                    <div className="flex gap-0.5">
                      {renderStars(avgRating || 0)}
                    </div>
                    {!ratingLoading && totalReviews > 0 && (
                      <span className="text-[10px] text-gray-400 font-medium">
                        ({totalReviews} reviews)
                      </span>
                    )}
                  </div>

                  {/* Lab Name */}
                  {test?.labName && (
                    <div className="text-[10px] text-gray-400 text-center truncate px-1 mt-1">
                      {test.labName}
                    </div>
                  )}

                  {/* Report Time Badge */}
                  {reportText && (
                    <div className="flex justify-center items-center mt-1.5">
                      <span className="text-[10px] font-medium text-red-600 bg-red-50 px-2.5 py-1 rounded-full border border-red-200">
                        {reportText}
                      </span>
                    </div>
                  )}

                  {/* 🔥 Price Section OR Coupon */}
                  <div className="mt-auto pt-2.5 border-t border-gray-100">
                    {hasDiscount && discount > 0 && isPriceAvailable ? (
                      // ---------- DISCOUNT SECTION ----------
                      <div className="flex justify-between items-center">
                        <div className="flex flex-col">
                          <div className="text-lg font-extrabold text-green-600">
                            ₹{sellingPrice}
                          </div>
                          {originalPrice > 0 && (
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs line-through text-gray-400">
                                ₹{originalPrice}
                              </span>
                              <span className="text-[11px] font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded-md">
                                Save ₹{discount}
                              </span>
                            </div>
                          )}
                        </div>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const item = buildCartItem(test);
                            inCart ? removeItem(item) : addWithReplaceCheck(item);
                          }}
                          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[10px] font-semibold border transition-all duration-200
                            ${inCart
                              ? "bg-red-50 text-red-600 border-red-500 hover:bg-red-100"
                              : "bg-blue-100 text-blue-600 border-blue-500 hover:bg-blue-200"
                            }`}
                        >
                          <FaShoppingCart size={10} />
                          {inCart ? "Remove" : "Add"}
                        </button>
                      </div>
                    ) : (
                      // ---------- COUPON SECTION ----------
                      <div className="flex flex-col gap-1">
                        {/* Price and Add to Cart in same row */}
                        <div className="flex justify-between items-center">
                          {/* Price */}
                          {isPriceAvailable && (
                            <div className="flex flex-col">
                              <div className="text-sm font-bold text-green-600">
                                ₹{sellingPrice || originalPrice}
                              </div>
                              {originalPrice > 0 && sellingPrice > 0 && originalPrice !== sellingPrice && (
                                <span className="text-xs line-through text-gray-400">
                                  ₹{originalPrice}
                                </span>
                              )}
                              {!hasDiscount && originalPrice > 0 && sellingPrice > 0 && originalPrice === sellingPrice && (
                                <span className="text-[9px] text-gray-400">No Discount</span>
                              )}
                            </div>
                          )}

                          {/* Add to Cart Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const item = buildCartItem(test);
                              inCart ? removeItem(item) : addWithReplaceCheck(item);
                            }}
                            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[10px] font-semibold border transition-all duration-200
                              ${inCart
                                ? "bg-red-50 text-red-600 border-red-500 hover:bg-red-100"
                                : "bg-blue-100 text-blue-600 border-blue-500 hover:bg-blue-200"
                              }`}
                          >
                            <FaShoppingCart size={10} />
                            {inCart ? "Remove" : "Add"}
                          </button>
                        </div>

                        {/* Coupon Component - Below Price & Add to Cart */}
                        <Coupon
                          labId={labId}
                          userId={getUserId()}
                          testPrice={originalPrice}
                          sellingPrice={sellingPrice}
                          discount={discount}
                          showCopyButton={false}
                          showAddToCart={false}
                          isInCart={inCart}
                          onAddToCart={() => {
                            const item = buildCartItem(test);
                            addWithReplaceCheck(item);
                          }}
                          onRemoveFromCart={() => {
                            const item = buildCartItem(test);
                            removeItem(item);
                          }}
                          className="w-full"
                          loadingText="Loading coupon..."
                          noCouponText="No coupon available"
                          labelText="🎫 No Discount - Coupon"
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* PAGINATION */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-1.5 mt-8 flex-wrap">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
                className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                  currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                Prev
              </button>

              {[...Array(totalPages)].map((_, i) => {
                const page = i + 1;
                const isActive = currentPage === page;
                return (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                      isActive
                        ? "bg-red-500 text-white border-red-500"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </button>
                );
              })}

              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
                className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                  currentPage === totalPages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* TRUST BADGES */}
      {tests.length > 0 && (
        <div className="mt-10 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="flex items-center justify-center gap-2 text-xs text-gray-600 bg-white/50 rounded-lg p-2 hover:bg-white transition-all duration-300">
              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 text-[10px] font-bold">✓</span>
              </div>
              <span>Verified Tests</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-600 bg-white/50 rounded-lg p-2 hover:bg-white transition-all duration-300">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 text-[10px] font-bold">⚡</span>
              </div>
              <span>Quick Reports</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-600 bg-white/50 rounded-lg p-2 hover:bg-white transition-all duration-300">
              <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 text-[10px] font-bold">🏆</span>
              </div>
              <span>NABL Certified Labs</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-600 bg-white/50 rounded-lg p-2 hover:bg-white transition-all duration-300">
              <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-red-600 text-[10px] font-bold">❤️</span>
              </div>
              <span>Home Collection</span>
            </div>
          </div>
        </div>
      )}

      {/* MODAL */}
      {selectedTestId && (
        <TestDetails
          id={selectedTestId}
          onClose={() => setSelectedTestId(null)}
        />
      )}

      {/* Login Popup for wishlist */}
      <LoginPopup
        isOpen={showLoginPopup}
        onClose={() => setShowLoginPopup(false)}
      />

      {LoginPopupComponent}
    </div>
  );
}